'use client';

import { MouseEvent, useState, memo } from 'react';
import clsx from 'clsx';
import { DataTestConfig } from '@headless-workspace/config';
import { ComponentMemo } from '@headless-workspace/typings';
import { GlowButton, GlowClickable, GlowIcon, GlowIconSkeleton, Icons, PropsWithAriaLabel } from '../../../../src';
import { PropsWithTestId } from '../../props';

export type GlowWishlistIconProps = PropsWithAriaLabel &
    PropsWithTestId & {
        onClick: () => void;
        isInWishlist: boolean;
        type?: 'button' | 'clickableIcon';
    };

const TIMER = 1500;

export const GlowWishlistButton: ComponentMemo<GlowWishlistIconProps> = memo(
    ({ ariaLabel, onClick, isInWishlist, type = 'clickableIcon', dataTestId, }) => {
        const [isUpdating, setIsUpdating] = useState(false);
        const dataTestIdToUse = dataTestId ?? DataTestConfig.testIds.wishlistButton;

        const onClickEvent = (event: MouseEvent) => {
            event.preventDefault();
            setIsUpdating(true);
            onClick();

            setTimeout(() => {
                setIsUpdating(false);
            }, TIMER);
        };

        if (type === 'button') {
            return (
                <GlowButton
                    fill={isInWishlist ? 'fill-text-primary text-text-primary' : 'none'}
                    variant={'secondary'}
                    dataTestId={dataTestIdToUse}
                    onClick={onClickEvent}
                    Icon={Icons.Heart}
                    SkeletonIcon={<GlowIconSkeleton Icon={Icons.HeartAnimation} type={'medium'} />}
                    isLoading={isUpdating}
                    disabled={isUpdating}
                />
            );
        }

        return (
            <GlowClickable
                dataTestId={dataTestIdToUse}
                className={'h-fit text-text-primary'}
                ariaLabel={ariaLabel}
                onClick={onClickEvent}
                disabled={isUpdating}
                content={{
                    icon: isUpdating ? (
                        <GlowIconSkeleton Icon={Icons.HeartAnimation} />
                    ) : (
                        <GlowIcon
                            Icon={Icons.Heart}
                            type={'large'}
                            fill={isInWishlist ? 'fill-text-primary text-text-primary' : 'none'}
                            className={clsx(
                                isInWishlist ? 'hover:fill-surface-brand-hover' : 'hover:fill-surface-primary-hover',
                            )}
                        />
                    ),
                }}
            />
        );
    },
);

GlowWishlistButton.displayName = 'GlowWishlistButton';
